import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetClose, SheetTitle } from '@/components/ui/sheet';
import {
  Edit, Eye, FileText, User, Calendar, Loader2, X, Lock, Plus, Sparkles,
} from 'lucide-react';
import SmartPagination from '../ui/smart-pagination';
import { PaginationParams } from '@/lib/types';
import NoteFormDrawer from '../clinic/NoteFormDrawer';
import { EmrClinicalNote, emrService } from '@/services/emrService';
import { useClinic } from '@/contexts/ClinicContext';
import { useOptionalEncounter } from '@/contexts/EncounterContext';
import { toast } from '@/hooks/use-toast';
import EncounterChip from '@/components/visit/EncounterChip';

interface PatientClinicalNotesTabProps {
  isLoading: boolean;
  notes: EmrClinicalNote[];
  pagination: PaginationParams;
  onPaginationChange: (page: number) => void;
  refetch: () => void;
  /**
   * Required when used outside of an EncounterContext (e.g. from
   * PatientDetail). Inside the visit cockpit, EncounterContext supplies
   * patientId so this prop can be omitted.
   */
  patientId?: string;
}

interface VisitGroup {
  key: string;
  label: string;
  appointmentId?: string;
  encounterId?: string;
  notes: EmrClinicalNote[];
}

const groupByVisit = (notes: EmrClinicalNote[]): VisitGroup[] => {
  const map = new Map<string, VisitGroup>();
  for (const n of notes) {
    const key = n.encounter_id
      || n.appointment_id
      || (n.note_date || n.created_at || '').slice(0, 10)
      || 'unknown';
    if (!map.has(key)) {
      map.set(key, {
        key,
        label: '',
        appointmentId: n.appointment_id,
        encounterId: n.encounter_id,
        notes: [],
      });
    }
    map.get(key)!.notes.push(n);
  }
  const groups = Array.from(map.values());
  for (const g of groups) {
    const primary = g.notes.find(n => !n.is_addendum) || g.notes[0];
    const date = primary?.note_date || primary?.created_at;
    const when = date ? new Date(date).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    }) : 'Undated';
    g.label = g.encounterId ? `Visit — ${when}` : `Chart note — ${when}`;
    g.notes.sort((a, b) => {
      // Parent first, then addendums chronologically.
      if (a.is_addendum !== b.is_addendum) return a.is_addendum ? 1 : -1;
      const da = new Date(a.note_date || a.created_at || 0).getTime();
      const db = new Date(b.note_date || b.created_at || 0).getTime();
      return da - db;
    });
  }
  groups.sort((a, b) => {
    const da = new Date(a.notes[0]?.note_date || a.notes[0]?.created_at || 0).getTime();
    const db = new Date(b.notes[0]?.note_date || b.notes[0]?.created_at || 0).getTime();
    return db - da;
  });
  return groups;
};

const formatDate = (dateString?: string) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const practitionerName = (note: EmrClinicalNote) =>
  note.practitioner
    ? `Dr. ${note.practitioner.first_name} ${note.practitioner.last_name}`
    : 'Unknown Practitioner';

const truncate = (text: string | null | undefined, max = 80) => {
  if (!text) return '—';
  return text.length <= max ? text : `${text.substring(0, max)}…`;
};

const PatientClinicalNotesTab = ({
  isLoading, notes, pagination, onPaginationChange, refetch, patientId: patientIdProp,
}: PatientClinicalNotesTabProps) => {
  const { clinic, isPractitioner, isAdmin } = useClinic();
  const encounterCtx = useOptionalEncounter();
  // patientId for new-note creation. Inside the cockpit it comes from
  // context; outside, the caller (PatientDetail) passes it.
  const newNotePatientId = patientIdProp || encounterCtx?.patientId || (notes[0]?.patient_id ?? '');
  const [selectedNote, setSelectedNote] = useState<EmrClinicalNote | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isNoteDrawerOpen, setIsNoteDrawerOpen] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [autoGenerateOnNext, setAutoGenerateOnNext] = useState(false);
  const [addendumDraft, setAddendumDraft] = useState('');
  const [isSavingAddendum, setIsSavingAddendum] = useState(false);

  const visitGroups = useMemo(() => groupByVisit(notes), [notes]);

  const openDetails = (note: EmrClinicalNote) => {
    setSelectedNote(note);
    setIsSheetOpen(true);
    setAddendumDraft('');
  };

  const closeDetails = () => {
    setIsSheetOpen(false);
    setTimeout(() => {
      setSelectedNote(null);
      setAddendumDraft('');
    }, 200);
  };

  const editNote = (note: EmrClinicalNote) => {
    setSelectedNote(note);
    setIsNoteDrawerOpen(true);
  };

  const saveAddendum = async () => {
    if (!selectedNote || !clinic?.id || !addendumDraft.trim()) return;
    setIsSavingAddendum(true);
    try {
      await emrService.createAddendum(clinic.id, selectedNote.id, addendumDraft.trim());
      toast({ title: 'Addendum added', variant: 'success' });
      setAddendumDraft('');
      refetch();
      closeDetails();
    } catch (error: any) {
      toast({
        title: 'Failed to add addendum',
        description: error?.response?.data?.error?.message || error.message || 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsSavingAddendum(false);
    }
  };

  const canWrite = isPractitioner || isAdmin;

  return (
    <>
      <div className="space-y-6">
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: '#6366f1' }}
              >
                <FileText className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Clinical Notes</h3>
                <p className="text-xs text-slate-500">
                  {isLoading
                    ? 'Loading…'
                    : `${pagination.totalItems || notes.length} total notes · grouped by visit`}
                </p>
              </div>
            </div>
            {canWrite && newNotePatientId && (
              <Button
                size="sm"
                onClick={() => {
                  setSelectedNote(null);
                  setIsCreatingNew(true);
                  setIsNoteDrawerOpen(true);
                }}
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                New Note
              </Button>
            )}
          </div>

          <div className="p-5">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-[#6366f1]" />
                <span className="ml-2 text-sm text-slate-500">Loading notes…</span>
              </div>
            ) : visitGroups.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <FileText className="h-6 w-6 text-slate-400" />
                </div>
                <p className="text-sm text-slate-500">No clinical notes available</p>
                {/* "Draft with IntelliHealth" CTA — only when we're inside an
                    active encounter and the practitioner can write notes. The
                    drawer auto-fills SOAP from the latest IntelliHealth
                    recommendation on open. */}
                {canWrite && encounterCtx?.encounterId && newNotePatientId && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-4 border-blue-200 text-blue-700 hover:bg-blue-50"
                    onClick={() => {
                      setSelectedNote(null);
                      setIsCreatingNew(true);
                      setAutoGenerateOnNext(true);
                      setIsNoteDrawerOpen(true);
                    }}
                  >
                    <Sparkles className="mr-1 h-3.5 w-3.5" />
                    Draft with IntelliHealth
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {visitGroups.map(group => (
                  <div key={group.key} className="space-y-2">
                    <div className="flex items-center gap-2 pl-1">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        {group.label}
                      </span>
                      <EncounterChip encounterId={group.encounterId} label='Open visit' />
                    </div>
                    {group.notes.map((note) => {
                      const isAddendum = note.is_addendum;
                      const isLocked = !!(note.is_locked || note.is_signed);
                      return (
                        <div
                          key={note.id}
                          className={`border rounded-lg p-4 bg-white ${
                            isAddendum ? 'border-slate-200 ml-6' : 'border-slate-200'
                          }`}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                            <div className="flex items-center gap-3">
                              <div
                                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: 'rgba(99, 57, 144, 0.15)' }}
                              >
                                <User className="h-5 w-5" style={{ color: 'rgb(99, 57, 144)' }} />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <div className="font-medium text-slate-900 text-sm">
                                    {practitionerName(note)}
                                  </div>
                                  {isAddendum && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-medium">
                                      Addendum
                                    </span>
                                  )}
                                  {note.is_signed && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">
                                      <Lock className="h-3 w-3" />
                                      Signed
                                    </span>
                                  )}
                                  {note.note_type && note.note_type !== 'soap' && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-50 text-slate-600 text-xs font-medium">
                                      {note.note_type}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                                  <Calendar className="h-3 w-3" />
                                  {formatDate(note.note_date || note.created_at)}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openDetails(note)}
                                className="h-8 px-3 text-xs"
                              >
                                <Eye className="h-3.5 w-3.5 mr-1" />
                                View
                              </Button>
                              {canWrite && !isLocked && !isAddendum && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => editNote(note)}
                                  className="h-8 px-3 text-xs"
                                >
                                  <Edit className="h-3.5 w-3.5 mr-1" />
                                  Edit
                                </Button>
                              )}
                              {canWrite && isLocked && !isAddendum && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openDetails(note)}
                                  className="h-8 px-3 text-xs text-[#6366f1] border-[#6366f1]"
                                >
                                  <Plus className="h-3.5 w-3.5 mr-1" />
                                  Addendum
                                </Button>
                              )}
                            </div>
                          </div>

                          {(note.subjective || note.objective || note.assessment || note.plan) ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                              <div className="flex gap-2">
                                <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded h-fit">S</span>
                                <p className="text-xs text-slate-600 leading-relaxed">{truncate(note.subjective)}</p>
                              </div>
                              <div className="flex gap-2">
                                <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded h-fit">O</span>
                                <p className="text-xs text-slate-600 leading-relaxed">{truncate(note.objective)}</p>
                              </div>
                              <div className="flex gap-2">
                                <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded h-fit">A</span>
                                <p className="text-xs text-slate-600 leading-relaxed">{truncate(note.assessment)}</p>
                              </div>
                              <div className="flex gap-2">
                                <span className="text-xs font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded h-fit">P</span>
                                <p className="text-xs text-slate-600 leading-relaxed">{truncate(note.plan)}</p>
                              </div>
                            </div>
                          ) : note.content ? (
                            <p className="text-xs text-slate-600 leading-relaxed mt-2">{truncate(note.content, 200)}</p>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>

          {pagination.totalPages > 1 && (
            <div className="px-5 py-4 border-t border-slate-100">
              <SmartPagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={onPaginationChange}
              />
            </div>
          )}
        </div>
      </div>

      {isNoteDrawerOpen && selectedNote && (
        <NoteFormDrawer
          open={isNoteDrawerOpen}
          onOpenChange={(open) => {
            setIsNoteDrawerOpen(open);
            if (!open) refetch();
          }}
          patientId={selectedNote.patient_id}
          onSubmit={refetch}
          note={selectedNote}
          appointmentId={selectedNote.appointment_id}
          encounterId={selectedNote.encounter_id}
        />
      )}

      {isNoteDrawerOpen && isCreatingNew && newNotePatientId && (
        <NoteFormDrawer
          open={isNoteDrawerOpen}
          onOpenChange={(open) => {
            setIsNoteDrawerOpen(open);
            if (!open) {
              setIsCreatingNew(false);
              setAutoGenerateOnNext(false);
              refetch();
            }
          }}
          patientId={newNotePatientId}
          onSubmit={refetch}
          note={null}
          appointmentId={encounterCtx?.appointmentId || undefined}
          encounterId={encounterCtx?.encounterId}
          autoGenerateOnOpen={autoGenerateOnNext}
        />
      )}

      <Sheet open={isSheetOpen} onOpenChange={closeDetails}>
        <SheetContent className="w-full !max-w-[600px] rounded-l-2xl p-0 flex flex-col gap-0 [&>button]:hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <SheetTitle className="font-semibold text-slate-900" style={{ fontSize: '1.05rem' }}>
                Clinical Note
              </SheetTitle>
              {selectedNote?.is_signed && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">
                  <Lock className="h-3 w-3" />
                  Signed
                </span>
              )}
            </div>
            <SheetClose asChild>
              <button className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors">
                <X className="h-5 w-5 text-slate-600" />
              </button>
            </SheetClose>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6">
            {selectedNote && (
              <div className="space-y-6">
                <div className="pb-5 border-b border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center bg-purple-100">
                      <User className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-900">
                        {practitionerName(selectedNote)}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5">
                        <Calendar className="h-3 w-3" />
                        {formatDate(selectedNote.note_date || selectedNote.created_at)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {['subjective', 'objective', 'assessment', 'plan'].map((field) => {
                    const value = (selectedNote as any)[field];
                    if (!value && !['subjective', 'objective', 'assessment', 'plan'].some(f => (selectedNote as any)[f])) return null;
                    const labels: Record<string, { letter: string; label: string; color: string }> = {
                      subjective: { letter: 'S', label: 'Subjective', color: 'blue' },
                      objective: { letter: 'O', label: 'Objective', color: 'emerald' },
                      assessment: { letter: 'A', label: 'Assessment', color: 'amber' },
                      plan: { letter: 'P', label: 'Plan', color: 'purple' },
                    };
                    const meta = labels[field];
                    return (
                      <div key={field} className="rounded-lg p-4 border border-slate-200">
                        <div className="flex items-center gap-2.5 mb-3">
                          <div className={`w-8 h-8 rounded-lg bg-${meta.color}-100 flex items-center justify-center`}>
                            <span className={`text-sm font-bold text-${meta.color}-600`}>{meta.letter}</span>
                          </div>
                          <h3 className="text-sm font-semibold text-slate-900">{meta.label}</h3>
                        </div>
                        <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">
                          {value || '—'}
                        </p>
                      </div>
                    );
                  })}

                  {selectedNote.content && !selectedNote.subjective && (
                    <div className="rounded-lg p-4 border border-slate-200">
                      <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">
                        {selectedNote.content}
                      </p>
                    </div>
                  )}
                </div>

                {canWrite && selectedNote.is_signed && !selectedNote.is_addendum && (
                  <div className="rounded-lg p-4 border border-slate-200 bg-amber-50/50">
                    <div className="flex items-center gap-2 mb-2">
                      <Plus className="h-4 w-4 text-amber-700" />
                      <h4 className="text-sm font-semibold text-amber-900">Add Addendum</h4>
                    </div>
                    <p className="text-xs text-amber-800 mb-3">
                      This note is signed and locked. An addendum creates a new, linked note —
                      the original stays intact.
                    </p>
                    <textarea
                      value={addendumDraft}
                      onChange={(e) => setAddendumDraft(e.target.value)}
                      placeholder="Addendum text…"
                      className="w-full min-h-[100px] rounded-md border border-amber-200 bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                      disabled={isSavingAddendum}
                    />
                    <div className="flex items-center justify-end mt-3">
                      <Button
                        onClick={saveAddendum}
                        disabled={!addendumDraft.trim() || isSavingAddendum}
                        className="rounded-lg"
                      >
                        {isSavingAddendum ? (
                          <span className="flex items-center gap-1.5">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Saving…
                          </span>
                        ) : 'Save Addendum'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end">
            <Button
              variant="outline"
              onClick={closeDetails}
              className="rounded-lg"
            >
              Close
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default PatientClinicalNotesTab;
